/**
 * Badge / StatusBadge / LiveDot — DS-41, DS-42.
 *
 * A badge is always light-background + dark-text of the same hue. Anything that
 * needs to shout is a colour on the row, not a solid chip.
 */

import { forwardRef } from 'react';
import type { CSSProperties, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { readableTextColor } from '@nova/shared';
import type {
  AppointmentStatus,
  CommissionStatementStatus,
  InvoiceStatus,
  LabOrderStatus,
  MessageStatus,
  PurchaseInvoiceStatus,
} from '@nova/shared';
import { useLang } from '../../i18n';
import { cn, withAlpha } from '../../lib/utils';

export type BadgeTone = 'slate' | 'blue' | 'emerald' | 'red' | 'amber' | 'purple' | 'cyan' | 'pink';

const TONE_CLASSES: Record<BadgeTone, string> = {
  slate: 'bg-slate-100 text-slate-700',
  blue: 'bg-blue-100 text-blue-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-800',
  purple: 'bg-purple-100 text-purple-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  pink: 'bg-pink-100 text-pink-700',
};

const DOT_CLASSES: Record<BadgeTone, string> = {
  slate: 'bg-slate-400',
  blue: 'bg-blue-500',
  emerald: 'bg-emerald-500',
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  purple: 'bg-purple-500',
  cyan: 'bg-cyan-500',
  pink: 'bg-pink-500',
};

// `color` is a user-defined hex (tag / provider colour), not the legacy HTML
// `color` attribute — omit the DOM one so the types do not conflict.
export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  tone?: BadgeTone;
  icon?: LucideIcon;
  /** Leading dot in the badge's own hue — for statuses read at a glance. */
  dot?: boolean;
  /**
   * User-defined hex (provider colour DS-53, appointment tag). Overrides `tone`;
   * the palette below only covers the eight system hues.
   */
  color?: string | null;
}

/**
 * DS-53 colours are chosen by the admin, so they can be any hex. We tint them to
 * 16% for the background and then ask `readableTextColor` whether the raw hue is
 * dark enough to sit on that tint: '#ffffff' means "this colour is dark", so the
 * hue itself becomes the label; otherwise the hue is too pale and we drop to
 * slate-900 rather than printing washed-out text.
 */
function customColorStyle(color: string): CSSProperties {
  return {
    backgroundColor: withAlpha(color, 0.16),
    color: readableTextColor(color) === '#ffffff' ? color : '#0f172a',
  };
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'slate', icon: Icon, dot = false, color, className, children, style, ...rest },
  ref,
) {
  const custom = color ? customColorStyle(color) : null;

  return (
    <span
      ref={ref}
      className={cn(
        // DS-41
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide',
        !color && TONE_CLASSES[tone],
        className,
      )}
      style={custom ? { ...custom, ...style } : style}
      {...rest}
    >
      {dot ? (
        <span
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', !color && DOT_CLASSES[tone])}
          style={color ? { backgroundColor: color } : undefined}
        />
      ) : null}
      {Icon ? <Icon className="h-3 w-3 shrink-0" aria-hidden="true" /> : null}
      {children}
    </span>
  );
});

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

interface StatusMeta {
  labelAr: string;
  labelEn: string;
  tone: BadgeTone;
}

/** APT-06/07 — the natural path warms up towards emerald; every exit is red or amber. */
const APPOINTMENT_STATUS_META: Record<AppointmentStatus, StatusMeta> = {
  PENDING: {
    labelAr: 'بانتظار مراجعة الاستقبال',
    labelEn: 'Pending review',
    tone: 'amber',
  },
  SCHEDULED: { labelAr: 'مجدول', labelEn: 'Scheduled', tone: 'slate' },
  CONFIRMED: { labelAr: 'مؤكد', labelEn: 'Confirmed', tone: 'blue' },
  ARRIVED: { labelAr: 'وصل', labelEn: 'Arrived', tone: 'cyan' },
  IN_SESSION: { labelAr: 'داخل الجلسة', labelEn: 'In session', tone: 'purple' },
  COMPLETED: { labelAr: 'مكتمل', labelEn: 'Completed', tone: 'emerald' },
  NO_ANSWER: { labelAr: 'لا يوجد رد', labelEn: 'No answer', tone: 'amber' },
  CANCELLED_BY_CLINIC: { labelAr: 'ملغي من العيادة', labelEn: 'Cancelled by clinic', tone: 'red' },
  CANCELLED_BY_PATIENT: { labelAr: 'ملغي من المريض', labelEn: 'Cancelled by patient', tone: 'red' },
  NO_SHOW: { labelAr: 'لم يحضر', labelEn: 'No show', tone: 'red' },
  REJECTED: { labelAr: 'مرفوض', labelEn: 'Rejected', tone: 'red' },
  RESCHEDULED: { labelAr: 'أعيدت جدولته', labelEn: 'Rescheduled', tone: 'amber' },
};

const INVOICE_STATUS_META: Record<InvoiceStatus, StatusMeta> = {
  DRAFT: { labelAr: 'مسودة', labelEn: 'Draft', tone: 'slate' },
  ISSUED: { labelAr: 'صادرة', labelEn: 'Issued', tone: 'blue' },
  PARTIALLY_PAID: { labelAr: 'مدفوعة جزئياً', labelEn: 'Partially paid', tone: 'amber' },
  PAID: { labelAr: 'مدفوعة', labelEn: 'Paid', tone: 'emerald' },
  VOIDED: { labelAr: 'ملغاة', labelEn: 'Voided', tone: 'red' },
  REFUNDED: { labelAr: 'مستردة', labelEn: 'Refunded', tone: 'purple' },
};

/** INV-76 — posting is irreversible, so POSTED reads as the settled state. */
const PURCHASE_INVOICE_STATUS_META: Record<PurchaseInvoiceStatus, StatusMeta> = {
  DRAFT: { labelAr: 'مسودة', labelEn: 'Draft', tone: 'slate' },
  POSTED: { labelAr: 'مرحّلة', labelEn: 'Posted', tone: 'emerald' },
  CANCELLED: { labelAr: 'ملغاة', labelEn: 'Cancelled', tone: 'red' },
};

/** COST-12 — approval freezes the statement; PAID closes it. */
const COMMISSION_STATEMENT_STATUS_META: Record<CommissionStatementStatus, StatusMeta> = {
  DRAFT: { labelAr: 'مسودة', labelEn: 'Draft', tone: 'slate' },
  APPROVED: { labelAr: 'معتمد', labelEn: 'Approved', tone: 'blue' },
  PAID: { labelAr: 'مدفوع', labelEn: 'Paid', tone: 'emerald' },
};

const MESSAGE_STATUS_META: Record<MessageStatus, StatusMeta> = {
  QUEUED: { labelAr: 'بالانتظار', labelEn: 'Queued', tone: 'slate' },
  SENT: { labelAr: 'مُرسلة', labelEn: 'Sent', tone: 'blue' },
  DELIVERED: { labelAr: 'وصلت', labelEn: 'Delivered', tone: 'emerald' },
  FAILED: { labelAr: 'فشلت', labelEn: 'Failed', tone: 'red' },
  CANCELLED: { labelAr: 'ملغاة', labelEn: 'Cancelled', tone: 'amber' },
};

const LAB_ORDER_STATUS_META: Record<LabOrderStatus, StatusMeta> = {
  ORDERED: { labelAr: 'مطلوب', labelEn: 'Ordered', tone: 'blue' },
  COLLECTED: { labelAr: 'تم السحب', labelEn: 'Collected', tone: 'cyan' },
  RESULTED: { labelAr: 'صدرت النتيجة', labelEn: 'Resulted', tone: 'emerald' },
  CANCELLED: { labelAr: 'ملغي', labelEn: 'Cancelled', tone: 'red' },
};

const STATUS_META: Record<StatusKind, Record<string, StatusMeta | undefined>> = {
  appointment: APPOINTMENT_STATUS_META,
  invoice: INVOICE_STATUS_META,
  purchaseInvoice: PURCHASE_INVOICE_STATUS_META,
  commissionStatement: COMMISSION_STATEMENT_STATUS_META,
  message: MESSAGE_STATUS_META,
  labOrder: LAB_ORDER_STATUS_META,
};

type StatusKind =
  | 'appointment'
  | 'invoice'
  | 'purchaseInvoice'
  | 'commissionStatement'
  | 'message'
  | 'labOrder';

interface StatusBadgeProps extends Omit<BadgeProps, 'tone' | 'children' | 'color'> {
  kind: StatusKind;
  /**
   * The enum member as the API returns it. Typed loosely on purpose: the client
   * must keep rendering if the server grows a status before the web build does.
   */
  value:
    | AppointmentStatus
    | InvoiceStatus
    | PurchaseInvoiceStatus
    | CommissionStatementStatus
    | MessageStatus
    | LabOrderStatus
    | string;
}

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge(
  { kind, value, ...rest },
  ref,
) {
  const lang = useLang();
  const meta = STATUS_META[kind][value];

  // An unmapped value still has to reach the screen — a blank cell in a list of
  // appointments is worse than an untranslated code.
  return (
    <Badge ref={ref} tone={meta?.tone ?? 'slate'} {...rest}>
      {(lang === 'EN' ? meta?.labelEn : meta?.labelAr) ?? value}
    </Badge>
  );
});

// ---------------------------------------------------------------------------
// LiveDot
// ---------------------------------------------------------------------------

interface LiveDotProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  tone?: BadgeTone;
  /** DS-42 — pulses only while the thing it reports on is actually live. */
  active?: boolean;
  /** Caption rendered beside the dot, already in the reader's language. */
  label?: string;
  /** Overrides `tone` with a provider/tag hex (DS-53). */
  color?: string | null;
}

export const LiveDot = forwardRef<HTMLSpanElement, LiveDotProps>(function LiveDot(
  { tone = 'emerald', active = true, label, color, className, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const dot = (
    <span
      className={cn(
        // DS-42 — 8px, the smallest circle that still reads as a state light.
        'inline-block h-2 w-2 shrink-0 rounded-full',
        !color && DOT_CLASSES[tone],
        active && 'animate-pulse',
      )}
      style={color ? { backgroundColor: color } : undefined}
    />
  );

  if (!label) {
    return (
      <span
        ref={ref}
        role="status"
        aria-label={active ? t('status.active') : t('status.inactive')}
        className={cn('inline-flex', className)}
        {...rest}
      >
        {dot}
      </span>
    );
  }

  return (
    <span ref={ref} role="status" className={cn('inline-flex items-center gap-1.5', className)} {...rest}>
      {dot}
      <span className="label-micro">{label}</span>
    </span>
  );
});
